import RestartAltIcon from '@mui/icons-material/RestartAlt';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';

import { StatusBadge } from '../../../../../components/atoms/StatusBadge';

interface StatusOption {
  code: string;
  name: string;
}

const StatusChip: React.FC<{ name: string; selected: boolean; onClick: () => void }> = ({
  name,
  selected,
  onClick,
}) => {
  const theme = useTheme();
  return (
    <ButtonBase
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      focusRipple
      sx={{
        px: 1.5,
        py: 0.75,
        borderRadius: 1,
        border: '1px solid',
        borderColor: selected ? theme.palette.v4.accent : theme.palette.v4.border,
        bgcolor: selected ? `${theme.palette.v4.accent}10` : 'transparent',
        display: 'flex',
        alignItems: 'center',
        '&:hover': { borderColor: theme.palette.v4.accent },
        '&.Mui-focusVisible': {
          borderColor: theme.palette.v4.accent,
          boxShadow: `0 0 0 2px ${theme.palette.v4.accent}40`,
        },
      }}
    >
      <StatusBadge status={name} />
    </ButtonBase>
  );
};

export interface ReopenJobDialogProps {
  open: boolean;
  onClose: () => void;
  currentStatus: string;
  /** Statuses the BFF allows as the reopen destination. */
  reopenStatuses: StatusOption[];
  loading: boolean;
  onSubmit: (statusCode: string, notes: string) => void;
  submitting: boolean;
}

/**
 * Dedicated dialog for reopening a closed job. Distinct from
 * StatusChangeDialog so the destination prompt, button copy and iconography
 * make the reopen intent obvious. When the BFF returns a single allowed
 * status it is auto-selected so the user only needs to confirm.
 */
export const ReopenJobDialog: React.FC<ReopenJobDialogProps> = ({
  open,
  onClose,
  currentStatus,
  reopenStatuses,
  loading,
  onSubmit,
  submitting,
}) => {
  const theme = useTheme();
  const [selectedStatus, setSelectedStatus] = useState('');
  const [notes, setNotes] = useState('');

  // Reset on each dialog open. Kept separate from the auto-select effect so
  // a React Query refetch (which produces a new reopenStatuses reference)
  // cannot clobber a selection the user has already made.
  useEffect(() => {
    if (open) {
      setNotes('');
      setSelectedStatus('');
    }
  }, [open]);

  // Auto-select when the BFF only offers one reopen destination — saves the
  // user a click in the common case. Uses functional setState + the prev ===
  // '' guard so this fires once after the statuses load and never overwrites
  // a deliberate user selection on subsequent refetches.
  useEffect(() => {
    if (open && reopenStatuses.length === 1) {
      const onlyCode = reopenStatuses[0].code;
      setSelectedStatus((prev) => (prev === '' ? onlyCode : prev));
    }
  }, [open, reopenStatuses]);

  const sortedStatuses = [...reopenStatuses].sort((a, b) => a.name.localeCompare(b.name));
  const noStatusesAvailable = !loading && sortedStatuses.length === 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{ fontWeight: 600, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <RestartAltIcon fontSize="small" />
        Reopen Job
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: '11px', color: 'text.secondary', mb: 0.5 }}>
            Current Status
          </Typography>
          <StatusBadge status={currentStatus} />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary', mb: 0.75 }}>
              Reopen To
            </Typography>

            {noStatusesAvailable && (
              <Typography sx={{ fontSize: '12px', color: 'text.secondary', mb: 2 }}>
                No reopen destinations are available for this job. Check your permissions or contact
                an administrator.
              </Typography>
            )}

            {sortedStatuses.length > 0 && (
              <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {sortedStatuses.map((status) => (
                  <StatusChip
                    key={status.code}
                    name={status.name}
                    selected={selectedStatus === status.code}
                    onClick={() => setSelectedStatus(status.code)}
                  />
                ))}
              </Box>
            )}

            <TextField
              label="Reason for reopening (optional)"
              multiline
              minRows={2}
              maxRows={4}
              fullWidth
              size="small"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: '13px' } }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onSubmit(selectedStatus, notes)}
          disabled={!selectedStatus || submitting || noStatusesAvailable}
          sx={{
            textTransform: 'none',
            bgcolor: theme.palette.v4.accent,
            '&:hover': { bgcolor: theme.palette.v4.accent, opacity: 0.9 },
          }}
        >
          {submitting ? (
            <CircularProgress size={16} sx={{ color: 'common.white' }} />
          ) : (
            'Reopen Job'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
